import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ChevronRight, PartyPopper } from "lucide-react";
import { api } from "../lib/api";

/**
 * Prioritised "what do I need to do NOW" action list.
 * Renders empty-state "All clear 🎉" when there are zero items.
 * Backend enforces role-specific aggregation.
 */
export default function ActionsNow({ compact = false, className = "" }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get("/actions/now").then((r) => { if (alive) setData(r.data); }).catch(() => { if (alive) setData({ items: [] }); });
        return () => { alive = false; };
    }, []);

    if (!data) return null;
    const items = data.items || [];

    if (items.length === 0) {
        return (
            <div className={`rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 mb-4 flex items-center gap-3 ${className}`} data-testid="actions-now-empty">
                <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                    <PartyPopper className="w-5 h-5" />
                </div>
                <div>
                    <div className="text-sm font-semibold text-emerald-800">All clear!</div>
                    <div className="text-xs text-emerald-700">Nothing pressing needs your attention right now.</div>
                </div>
            </div>
        );
    }

    // Colour ring by priority (1 highest → red, 2 → amber, 3+ → gray)
    function ring(p) {
        if (p === 1) return "bg-red-100 text-red-700";
        if (p === 2) return "bg-amber-100 text-amber-800";
        return "bg-gray-100 text-gray-600";
    }

    return (
        <div className={`rounded-xl border border-gray-200 bg-white p-3 sm:p-4 mb-4 ${className}`} data-testid="actions-now">
            <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider font-semibold text-[#1E3355] flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#E02B2B]" />
                    What to do now
                </div>
                <div className="text-[10px] text-gray-500">{items.length} pending</div>
            </div>
            <ul className="divide-y divide-gray-100">
                {items.slice(0, compact ? 5 : 12).map((it, i) => (
                    <li key={i} data-testid={it.testid || `action-${i}`}>
                        <Link
                            to={it.to}
                            className="flex items-center gap-3 py-2.5 hover:bg-gray-50 -mx-2 px-2 rounded"
                        >
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${ring(it.priority)}`}>
                                {it.priority}
                            </div>
                            <div className="flex-1 min-w-0 text-sm text-gray-800 truncate">{it.label}</div>
                            <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}
